import { effect } from '@chakra-ui/react';
import { AttachmentInfo, SchoolDate, UploadToken } from "./value_object";
import heic2any from 'heic2any';

/**
 * 處理檔案上傳
 * 參考：https://uploadcare.com/blog/how-to-upload-file-in-react/
 * 參考：https://github.com/ischoolinc/storage.1campus.net/blob/main/demo/README.md
 * */
export class FileStorageHelper {
  /** 上傳檔案 */
  static async uploadFile(
    sourceFile: File | null,
    expiryHours?: number
  ): Promise<AttachmentInfo | undefined> {
    if (!sourceFile) return undefined;
    console.log("sourceFile", sourceFile);
    console.log(`sourceFile size: ${sourceFile.size}`);
    // Check file size (5MB = 5 * 1024 * 1024 bytes)
    // const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    // if (file.size > maxSize) {
    //     const ex: any = new Error(`File size exceeds the maximum limit of 5MB. Current file size: ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
    //     ex.code = 413;
    //     throw ex;
    // }

    try {
          // 如果是 HEIF/HEIC 格式，先轉換成 JPG
    let newfile = sourceFile;
    if (FileStorageHelper.isHEIFFormat(sourceFile)) {
      console.log("偵測到 HEIF/HEIC 格式，開始轉換成 JPG...");
      newfile = await FileStorageHelper.convertHEICtoJPG(sourceFile);
      console.log("轉換完成", newfile);
    }

      // 如果是圖檔且超過指定大小，就自動縮小檔案。否則就回傳原本檔案。
      const file = await FileStorageHelper.resizeImageFile(newfile);
      console.log("resizeImageFile", file);
      const formData = new FormData();
      formData.append("file", file!);
      const uploadToken: UploadToken = await FileStorageHelper.getUploadToken(expiryHours);

      const uploadUrl = `https://storage.1campus.net/file?upload_token=${uploadToken.token}`;

      const response = await fetch(uploadUrl, {
        method: "POST",
        body: formData,
      });

      // fetch api 的錯誤不能使用 try ... catch 攔截，必須使用 response.ok 判斷。
      // 請參考：https://dev.to/dionarodrigues/fetch-api-do-you-really-know-how-to-handle-errors-2gj0
      if (!response.ok) {
        const ex = await response.json();
        ex.code = response.status;
        throw ex;
      }
      const result = (await response.json())[0];
      result.type = file!.type; // 手動加上 mime type
      return result;

    } catch (err: any) {
      console.error("uploadFile 發生錯誤:", err);
      throw err;
    }
  }

  /** 判斷是否為 HEIF/HEIC 格式 */
static isHEIFFormat(file: File): boolean {
  const fileName = file.name.toLowerCase();
  const heifExtensions = ['.heif', '.heic'];
  return heifExtensions.some(ext => fileName.endsWith(ext)) || 
         file.type === 'image/heif' || 
         file.type === 'image/heic';
}

/** 將 HEIF/HEIC 轉換成 JPG */
static async convertHEICtoJPG(file: File): Promise<File> {
  try {
    const convertedBlob = await heic2any({
      blob: file,
      toType: 'image/jpeg',
      quality: 0.9
    }) as Blob;

    // 將 Blob 轉換成 File
    const fileName = file.name.replace(/\.(heif|heic)$/i, '.jpg');
    return new File([convertedBlob], fileName, { type: 'image/jpeg' });
  } catch (error) {
    console.error("HEIF/HEIC 轉換失敗:", error);
    throw new Error("無法轉換 HEIF/HEIC 格式，請使用其他圖片格式");
  }
}
  /** 如果圖檔超過指定大小，則壓縮檔案到寬度 600px */
  static async resizeImageFile(file: File): Promise<File | undefined> {
    if (!file) {
      return file;
    }

    // 檢查是否為圖片格式
    const isImage = FileStorageHelper.isImageFormat(file);
    if (!isImage) {
      console.warn(`檔案格式錯誤：${file.type} 不是圖片格式。`);
      return file;
    } else {
      console.log(`圖片格式正確：${file.type}`);
    }

    // 檢查是否超過檔案大小限制
    const isTooLarge = FileStorageHelper.exceedImageSize(file);
    if (isTooLarge) {
      console.warn(`檔案過大：${(file.size / 1024 / 1024).toFixed(2)} MB 超過 5MB，將自動壓縮。`);
      const targetFile = await FileStorageHelper.shrinkImage(file, 600);
      console.log(`壓縮後檔案大小：${(targetFile?.size ?? 0) / 1024 / 1024} MB`);
      return targetFile;
    } else {
      console.log(`檔案大小合格：${(file.size / 1024 / 1024).toFixed(2)} MB`);
    }

    // if (FileStorageHelper.isImageFormat(file) && FileStorageHelper.exceedImageSize(file)) {
    //     console.log('file size is too large. ')
    //     const targetFile = await FileStorageHelper.shrinkImage(file, 600);
    //     console.log(` convert file size from ${file.size} to ${targetFile?.size }`)
    //     return targetFile ;
    // } else {
    //     return file ;
    // }

    return file;
  }

  /** 判斷是否超過設定大小 */
  static exceedImageSize(file: File): boolean {
    // Check file size (5MB = 5 * 1024 * 1024 bytes)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    return file.size > maxSize;
  }

  /** 從選取檔案的 mime type 判斷是否是圖片 */
  static isImageFormat(file: File | undefined): boolean {
    if (!file) {
      return false;
    }
    
    // 檢查 MIME type
    if (file.type.indexOf("image/") > -1) {
      return true;
    }
    
    // 檢查副檔名（處理 HEIF/HEIC 等瀏覽器可能無法正確識別 MIME type 的格式）
    const fileName = file.name.toLowerCase();
    console.log("檔案名稱:", fileName);
    const imageExtensions = ['.heif', '.heic', '.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'];
    return imageExtensions.some(ext => fileName.endsWith(ext));
  }

  /** 以ID取得檔案的metadata */
  static async getFileMetadata(url: string): Promise<any> {
    const resp = await fetch(url + "/metadata");
    if (!resp.ok)
      throw new Error(
        "getFileMetadata()Fetch failed with status " + resp.status
      );
    return await resp.json();
  }

  /** 取得上傳檔案用的 upload token */
  static async getUploadToken(expiry_hours?: number): Promise<UploadToken> {
    const url = `/service/file/upload_token?expiry_hours=${expiry_hours ?? 8760}`;
    const resp = await fetch(url);
    if (!resp.ok)
      throw new Error(
        "getUploadToken() Fetch failed with status " + resp.status
      );
    return await resp.json();
  }

  /** 把 image dataurl 轉換成 File 物件 */
  static convertDataURLtoFile(
    dataurl: string | undefined,
    filename: string
  ): File | undefined {
    // data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/uk+td.....=
    if (!dataurl) {
      return undefined;
    }

    const arr = dataurl.split(",");
    // 找 mime.
    const temp = arr[0];
    const mime_temp = temp.split(":")[1];
    let mime = mime_temp.split(";")[0];

    const bstr = atob(arr[arr.length - 1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], `${filename}.${mime[0]}`, { type: mime });
  }

  /** 縮小圖片檔案到指定大小， https://www.youtube.com/watch?v=bXf_UdyDzSA */
  static async shrinkImage(
    file: File,
    maxWidth: number = 400
  ): Promise<File | undefined> {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = function (event) {
        if (event.target) {
          const imgElement = document.createElement("img") as HTMLImageElement;
          imgElement.src = event.target.result as string;

          imgElement.onload = (e) => {
            if (e.target) {
              const canvas = document.createElement(
                "canvas"
              ) as HTMLCanvasElement;
              const imgTarget = e.target as HTMLImageElement;
              const scaleSize = maxWidth / imgTarget.width;
              canvas.width = maxWidth;
              canvas.height = imgTarget.height * scaleSize;
              const ctx = canvas.getContext("2d");
              ctx?.drawImage(imgTarget, 0, 0, canvas.width, canvas.height);
              const srcEncoded = ctx?.canvas.toDataURL("image/jpeg");
              const finalFile = FileStorageHelper.convertDataURLtoFile(
                srcEncoded,
                file.name
              );
              resolve(finalFile);
            } else {
              resolve(file);
            }
          };
        } else {
          resolve(file);
        }
      };
    });
  }
}
